"use client";

import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs";
import { cn } from "../lib/utils";

interface EnhancedTabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}

interface EnhancedTabsListProps {
  className?: string;
  children: React.ReactNode;
}

interface EnhancedTabsTriggerProps {
  value: string;
  className?: string;
  children: React.ReactNode;
  variant?: "blue" | "green" | "purple" | "orange" | "red";
}

interface EnhancedTabsContentProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

// Color variants for different tab themes - Enhanced for better visibility
const tabVariants = {
  blue: {
    active: "data-[state=active]:bg-blue-700 data-[state=active]:text-gray-800 data-[state=active]:border-blue-900 data-[state=active]:shadow-lg",
    hover: "hover:bg-blue-100 hover:text-blue-800",
    inactive: "bg-card text-gray-800 border-gray-300",
  },
  green: {
    active: "data-[state=active]:bg-green-700 data-[state=active]:text-white data-[state=active]:border-green-900 data-[state=active]:shadow-lg",
    hover: "hover:bg-green-100 hover:text-green-800",
    inactive: "bg-card text-gray-800 border-gray-300",
  },
  purple: {
    active: "data-[state=active]:bg-purple-700 data-[state=active]:text-white data-[state=active]:border-purple-900 data-[state=active]:shadow-lg",
    hover: "hover:bg-purple-100 hover:text-purple-800",
    inactive: "bg-card text-gray-800 border-gray-300",
  },
  orange: {
    active: "data-[state=active]:bg-orange-700 data-[state=active]:text-white data-[state=active]:border-orange-900 data-[state=active]:shadow-lg",
    hover: "hover:bg-orange-100 hover:text-orange-800",
    inactive: "bg-card text-gray-800 border-gray-300",
  },
  red: {
    active: "data-[state=active]:bg-red-700 data-[state=active]:text-white data-[state=active]:border-red-900 data-[state=active]:shadow-lg",
    hover: "hover:bg-red-100 hover:text-red-800",
    inactive: "bg-card text-gray-800 border-gray-300",
  },
};

export function EnhancedTabs({ defaultValue, value, onValueChange, className, children }: EnhancedTabsProps) {
  return (
    <Tabs defaultValue={defaultValue} value={value} onValueChange={onValueChange} className={cn("w-full", className)}>
      {children}
    </Tabs>
  );
}

export function EnhancedTabsList({ className, children }: EnhancedTabsListProps) {
  return (
    <TabsList className={cn(
      "",
      className
    )}>
      {children}
    </TabsList>
  );
}

export function EnhancedTabsTrigger({ 
  value, 
  className, 
  children, 
  variant = "blue" 
}: EnhancedTabsTriggerProps) {
  const variantStyles = tabVariants[variant];
  
  return (
    <TabsTrigger
      value={value}
      className={cn(
        // Base styles - preserve original sizing
        "flex items-center gap-2 transition-colors duration-200",
        // Inactive state with enhanced contrast
        variantStyles.inactive,
        // Active state with enhanced colors
        variantStyles.active,
        // Hover state with enhanced colors
        variantStyles.hover,
        // Font weight for better visibility
        "data-[state=active]:font-semibold",
        className
      )}
    >
      {children}
    </TabsTrigger>
  );
}

export function EnhancedTabsContent({ value, className, children }: EnhancedTabsContentProps) {
  return (
    <TabsContent value={value} className={cn("mt-6", className)}>
      {children}
    </TabsContent>
  );
}

// Export all components as a single object for convenience
export const EnhancedTabsComponents = {
  Tabs: EnhancedTabs,
  TabsList: EnhancedTabsList,
  TabsTrigger: EnhancedTabsTrigger,
  TabsContent: EnhancedTabsContent,
};
